import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { getConflictText, parseContactConflict } from "../../utils/contactConflict";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  hint: {
    marginBottom: theme.spacing(2),
  },
}));

/**
 * Só dígitos, 10 a 13 caracteres — a mesma janela que o backend aceita
 * (normalizePhoneNumber.ts): 10/11 sem DDI (o backend completa com 55) ou
 * 12/13 já com DDI. A validação de verdade é sempre a do backend; isto é só
 * para não deixar o usuário submeter algo obviamente errado.
 */
const ContactSchema = Yup.object().shape({
  name: Yup.string().trim().required("Informe o nome do cliente"),
  number: Yup.string()
    .transform((value) => (value || "").replace(/\D/g, ""))
    .matches(/^\d{10,13}$/, "Informe DDD + telefone (ex: 48 99999-9999)")
    .required("Informe o telefone do cliente"),
});

/**
 * Cadastro de contato direto do card do Kanban, para apólice ainda sem
 * contato vinculado — o caminho principal daqui para frente, ao lado da
 * conciliação em lote por similaridade de nome.
 *
 * Reaproveita POST /policy-staging/:id/create-contact (mesmo endpoint da
 * tela de conciliação). O nome vem pré-preenchido com clientNameRaw da
 * apólice mas é editável; o backend sanitiza e rejeita vazio antes de
 * gravar. O telefone é sempre obrigatório aqui — diferente da conciliação em
 * lote, que aceita deixar em branco para um contato pendingPhone, este
 * caminho existe justamente para o card sair do estado "sem contato".
 */
const CreateContactModal = ({ open, onClose, opportunity, onSuccess }) => {
  const classes = useStyles();
  // Conflito da regra R (409 contato_existente): nada foi alterado.
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (open) setConflict(null);
  }, [open]);

  const stagingId = opportunity?.policy?.importStaging?.id;
  const policyId = opportunity?.policy?.id;
  const clientName = opportunity?.policy?.clientNameRaw || "";

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { data } = await api.post(`/policy-staging/${stagingId}/create-contact`, {
        name: values.name.trim(),
        number: values.number,
      });
      toast.success("Contato cadastrado. O card já está atualizado.");
      onSuccess(policyId, data.contact);
      onClose();
    } catch (err) {
      const parsed = err?.response?.status === 409 ? parseContactConflict(err.response?.data) : null;
      if (parsed) {
        setConflict(parsed);
      } else {
        toastError(err);
      }
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cadastrar contato</DialogTitle>
      {conflict ? (
        <>
          <DialogContent>
            <Typography variant="body2" color="textSecondary">
              {getConflictText(conflict.reason, i18n.t.bind(i18n))}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConflict(null)}>Voltar</Button>
            <Button onClick={onClose} color="primary" variant="contained">
              {i18n.t("contactConflict.close")}
            </Button>
          </DialogActions>
        </>
      ) : (
      <Formik
        enableReinitialize
        initialValues={{ name: clientName, number: "" }}
        validationSchema={ContactSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, isSubmitting }) => (
          <Form>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" className={classes.hint}>
                Esta apólice ainda não tem um cliente de WhatsApp vinculado. Confirme o nome e
                informe o telefone para o card entrar no fluxo normal do Kanban.
              </Typography>

              <Field
                as={TextField}
                id="create-contact-name"
                className={classes.field}
                name="name"
                label="Nome"
                fullWidth
                variant="outlined"
                value={values.name}
                onChange={handleChange}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />

              <Field
                as={TextField}
                id="create-contact-number"
                className={classes.field}
                name="number"
                label="Telefone"
                placeholder="(48) 99999-9999"
                fullWidth
                variant="outlined"
                value={values.number}
                onChange={handleChange}
                error={touched.number && Boolean(errors.number)}
                helperText={touched.number && errors.number}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} /> : "Cadastrar"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
      )}
    </Dialog>
  );
};

export default CreateContactModal;
